<template>
  <section class="bills-panel">
    <header class="bills-heading">
      <div>
        <h1>{{ $t('账单') }}</h1>
        <p>{{ $t('每一笔收益、扣费与账户变化，透明可查') }}</p>
      </div>
      <div class="bills-heading__actions">
        <select
          v-model="selectedMonth"
          :aria-label="$t('账单月份')"
          :disabled="periodLoading || recordLoading"
          @change="handleMonthChange"
        >
          <option
            v-for="item in monthOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
        <button
          class="export-button"
          type="button"
          :disabled="periodLoading || recordLoading || loading"
          @click="exportBills"
        >
          {{ $t('导出当月账单') }}
        </button>
      </div>
    </header>

    <div class="bill-summary up-card">
      <article>
        <div class="summary-title">
          <span class="summary-icon is-fee"><i class="el-icon-tickets" /></span>
          <span>{{ $t('本月服务费 (USDT)') }}</span>
        </div>
        <strong>{{ serviceFeeText }}</strong>
        <p>
          <span v-if="serviceFeeChange !== null" :class="serviceFeeChangeClass">
            {{ serviceFeeChangeText }}
          </span>
          <span v-else class="summary-change is-neutral">--</span>
          <small>{{ $t('较上月') }}</small>
        </p>
      </article>

      <article>
        <div class="summary-title">
          <span class="summary-icon is-profit"><i class="el-icon-lightning" /></span>
          <span>{{ $t('本月结算收益 (USDT)') }}</span>
          <em><i />{{ $t('实时') }}</em>
        </div>
        <strong :class="settlementProfitClass">{{ settlementProfitText }}</strong>
        <p><small>{{ $t(`共结算 ${settlementCount} 笔`) }}</small></p>
      </article>

      <article>
        <div class="summary-title">
          <span class="summary-icon is-card"><i class="el-icon-bank-card" /></span>
          <span>{{ $t('可用点卡') }}</span>
        </div>
        <strong>{{ pointCardText }} <small>{{ $t('点') }}</small></strong>
        <p><small>{{ cardDaysText }}</small></p>
      </article>
    </div>

    <div class="bill-detail-title">
      <i />
      <h2>{{ $t('账单明细') }}</h2>
      <span>{{ $t('逐笔可查') }}</span>
    </div>

    <article class="bill-detail up-card">
      <div class="bill-toolbar">
        <div class="bill-tabs">
          <button
            :class="{ active: activeTab === 'records' }"
            type="button"
            @click="changeTab('records')"
          >
            <span class="bill-tab-label--desktop">{{ $t('燃料 / 点卡记录') }}</span>
            <span class="bill-tab-label--mobile">{{ $t('燃料 / 点卡') }}</span>
          </button>
          <button
            :class="{ active: activeTab === 'flow' }"
            type="button"
            @click="changeTab('flow')"
          >
            <span class="bill-tab-label--desktop">{{ $t('充值 / 提现记录') }}</span>
            <span class="bill-tab-label--mobile">{{ $t('充值 / 提现') }}</span>
          </button>
          <button
            :class="{ active: activeTab === 'account' }"
            type="button"
            @click="changeTab('account')"
          >
            <span class="bill-tab-label--desktop">{{ $t('购买点卡 / 资金划转记录') }}</span>
            <span class="bill-tab-label--mobile">{{ $t('购买点卡/资金划转') }}</span>
          </button>
        </div>

        <div v-if="activeTab === 'records'" class="bill-filters">

          <select v-model="tradeType" :aria-label="$t('交易类型')">
            <option value="all">{{ $t('全部交易') }}</option>
            <option value="hedge">{{ $t('合约AI') }}</option>
            <option value="term">{{ $t('期现套利') }}</option>
            <option value="grid">{{ $t('现货网格') }}</option>
            <option value="martin">{{ $t('现货马丁') }}</option>
          </select>
        </div>
        <label v-else-if="activeTab === 'flow'" class="account-record-filter">
          <span>{{ $t('类别查询') }}</span>
          <select
            v-model="flowCategory"
            :disabled="loading"
            :aria-label="$t('充值提现记录类型')"
            @change="changeFlowCategory(flowCategory)"
          >
            <option value="deposit">{{ $t('充值记录') }}</option>
            <option value="withdraw">{{ $t('提现记录') }}</option>
          </select>
        </label>
        <label v-else class="account-record-filter">
          <span>{{ $t('类别查询') }}</span>
          <select
            v-model="accountCategory"
            :disabled="accountLoading"
            :aria-label="$t('购买点卡与燃料划转记录类型')"
            @change="changeAccountCategory"
          >
            <option value="pointCard">{{ $t('购买点卡') }}</option>
            <option value="fuelTransfer">{{ $t('燃料划转') }}</option>
          </select>
        </label>
      </div>

      <template v-if="activeTab === 'records'">
        <div class="records-table__head">
          <span>{{ $t('时间') }}</span>
          <span>{{ $t('账单类型') }}</span>
          <span>{{ $t('收益 (USDT)') }}</span>
          <span>{{ $t('扣除') }}</span>
          <span>{{ $t('交易类型') }}</span>
          <span>{{ $t('余额 (USDT)') }}</span>
        </div>
        <div
          v-for="row in pagedRecordRows"
          :key="row.key"
          class="records-table__row"
        >
          <span :data-label="$t('时间')">{{ row.time }}</span>
          <span :data-label="$t('账单类型')"><i class="bill-type" :class="row.typeClass">{{ row.type }}</i></span>
          <strong :data-label="$t('收益 (USDT)')" class="positive">{{ row.gain }}</strong>
          <strong :data-label="$t('扣除')" class="negative">{{ row.fee }}</strong>
          <span :data-label="$t('交易类型')">{{ row.trade }}</span>
          <span :data-label="$t('余额 (USDT)')" class="align-right">{{ row.balance }}</span>
        </div>
        <div v-if="!pagedRecordRows.length" class="bill-empty">
          {{ $t(recordLoading ? '正在同步账单记录…' : '暂无符合条件的账单记录') }}
        </div>
      </template>

      <template v-else-if="activeTab === 'flow'">
        <div class="flow-table__head">
          <span>{{ $t('时间') }}</span>
          <span>{{ $t('订单类型') }}</span>
          <span>{{ $t('账户') }}</span>
          <span>{{ $t('币种') }}</span>
          <span>{{ $t('数量') }}</span>
          <span>{{ $t('渠道 · 地址') }}</span>
          <span>{{ $t('状态') }}</span>
        </div>
        <div
          v-for="row in pagedFlowRows"
          :key="row.key"
          class="flow-table__row"
        >
          <span :data-label="$t('时间')">{{ row.time }}</span>
          <strong :data-label="$t('订单类型')">{{ row.kind }}</strong>
          <span :data-label="$t('账户')">{{ row.account }}</span>
          <strong :data-label="$t('币种')">{{ row.coin }}</strong>
          <strong :data-label="$t('数量')" :class="row.amountClass">{{ row.amount }}</strong>
          <span :data-label="$t('渠道 · 地址')" class="flow-channel" :title="row.fullChannel">{{ row.channel }}</span>
          <span :data-label="$t('状态')"><i class="flow-status" :class="row.statusClass">{{ row.status }}</i></span>
        </div>
        <div v-if="!pagedFlowRows.length" class="bill-empty">
          {{ $t(loading ? `正在同步${flowRecordLabel}…` : `暂无符合条件的${flowRecordLabel}`) }}
        </div>
      </template>

      <template v-else>
        <div class="account-record-table__head">
          <span>{{ $t('时间') }}</span>
          <span>{{ $t('记录类型') }}</span>
          <span>{{ $t('账户') }}</span>
          <span>{{ $t('币种') }}</span>
          <span>{{ $t('数量') }}</span>
          <span>{{ accountFinalColumnLabel }}</span>
        </div>
        <div
          v-for="row in pagedAccountRows"
          :key="row.key"
          class="account-record-table__row"
        >
          <span :data-label="$t('时间')">{{ row.time }}</span>
          <strong :data-label="$t('记录类型')">{{ row.kind }}</strong>
          <span :data-label="$t('账户')">{{ row.account }}</span>
          <strong :data-label="$t('币种')">{{ row.coin }}</strong>
          <strong :data-label="$t('数量')" :class="row.amountClass">{{ row.amount }}</strong>
          <span :data-label="$t(accountFinalColumnLabel)" class="align-right">{{ row.balance }}</span>
        </div>
        <div v-if="!pagedAccountRows.length" class="bill-empty">
          {{ $t(accountLoading ? `正在同步${accountRecordLabel}…` : `暂无${accountRecordLabel}`) }}
        </div>
      </template>

      <footer class="bill-pagination">
        <span>{{ $t(`共 ${activeTotal} 条记录 · 第 ${currentPage} / ${pageCount} 页`) }}</span>
        <div>
          <button
            type="button"
            :disabled="currentPage === 1 || activeTableLoading"
            @click="goPage(currentPage - 1)"
          >
            {{ $t('上一页') }}
          </button>
          <button
            v-for="pageNumber in pageButtons"
            :key="pageNumber"
            type="button"
            :disabled="activeTableLoading"
            :class="{ active: pageNumber === currentPage }"
            @click="goPage(pageNumber)"
          >
            {{ pageNumber }}
          </button>
          <button
            type="button"
            :disabled="currentPage === pageCount || activeTableLoading"
            @click="goPage(currentPage + 1)"
          >
            {{ $t('下一页') }}
          </button>
        </div>
      </footer>
    </article>
  </section>
</template>

<script>
import {
  finiteNumber,
  firstNumber,
  firstText,
  formatNumber
} from '@/utils/portal-data'

const PRODUCT_NAMES = {
  hedge: '合约AI',
  futures_x: 'UPlus-X 合约',
  term: '期现套利',
  Term: '期现套利',
  grid: '现货网格',
  martin: '现货马丁',
  astra: 'UPlus-Astra AI',
  sj: '黄金三角',
  bb: 'BB 交易',
  ub: 'UB 交易'
}

export default {
  name: 'BillsPanel',
  props: {
    rows: { type: Array, default: () => [] },
    billRows: { type: Array, default: () => [] },
    trades: { type: Array, default: () => [] },
    balances: {
      type: Object,
      default: () => ({ total: null, fund: null, fuel: null, card: null })
    },
    earnings: {
      type: Object,
      default: () => ({ monthProfit: null })
    },
    period: {
      type: Object,
      default: () => ({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 })
    },
    loading: { type: Boolean, default: false },
    recordLoading: { type: Boolean, default: false },
    recordPagination: {
      type: Object,
      default: () => ({ page: 1, pageSize: 10, total: 0 })
    },
    recordSummary: {
      type: Object,
      default: () => ({ allDeposit: null })
    },
    flowPagination: {
      type: Object,
      default: () => ({
        deposit: { page: 1, pageSize: 10, total: 0 },
        withdraw: { page: 1, pageSize: 10, total: 0 }
      })
    },
    accountRecords: {
      type: Object,
      default: () => ({ pointCard: [], fuelTransfer: [] })
    },
    accountLoading: { type: Boolean, default: false },
    accountPagination: {
      type: Object,
      default: () => ({
        pointCard: { page: 1, pageSize: 10, total: 0 },
        fuelTransfer: { page: 1, pageSize: 10, total: 0 }
      })
    },
    initialCategory: { type: String, default: 'all' },
    periodLoading: { type: Boolean, default: false }
  },
  data() {
    const year = Number(this.period.year) || new Date().getFullYear()
    const month = Number(this.period.month) || new Date().getMonth() + 1
    const initialCategory = String(this.initialCategory || '')
    return {
      activeTab: ['deposit', 'withdraw'].includes(initialCategory) ? 'flow' : 'records',
      selectedMonth: `${year}-${String(month).padStart(2, '0')}`,
      query: '',
      billType: initialCategory === 'fee'
        ? 'fee'
        : (initialCategory === 'profit' ? 'profit' : 'all'),
      tradeType: 'all',
      flowCategory: initialCategory === 'withdraw' ? 'withdraw' : 'deposit',
      accountCategory: 'pointCard'
    }
  },
  computed: {
    monthOptions() {
      const now = new Date()
      const baseYear = now.getFullYear()
      const baseMonth = now.getMonth() + 1
      return Array.from({ length: 24 }, (_, index) => {
        const date = new Date(baseYear, baseMonth - 1 - index, 1)
        return {
          value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
          label: this.$t(`${date.getFullYear()}年${date.getMonth() + 1}月`)
        }
      })
    },
    monthRecordRows() {
      return this.billRows.filter(row =>
        ['profit', 'fee'].includes(row.category) &&
        this.inSelectedMonth(row.time)
      )
    },
    recordRows() {
      const query = this.query.toLowerCase()
      return this.billRows
        .map((row, index) => this.toRecordRow(row, index))
        .filter(row => {
          const matchesType = this.billType === 'all' || row.typeKey === this.billType
          const matchesTrade = this.tradeType === 'all' || row.productType === this.tradeType
          const matchesQuery = !query || `${row.trade} ${row.type} ${row.rawId}`.toLowerCase().includes(query)
          return matchesType && matchesTrade && matchesQuery
        })
    },
    flowRows() {
      return this.rows
        .filter(row => row.category === this.flowCategory)
        .map((row, index) => this.toFlowRow(row, index))
    },
    pagedRecordRows() {
      return this.recordLoading ? [] : this.recordRows
    },
    pagedFlowRows() {
      return this.loading ? [] : this.flowRows
    },
    accountRows() {
      const rows = this.accountRecords[this.accountCategory]
      return (Array.isArray(rows) ? rows : [])
        .map((row, index) => this.toAccountRecordRow(row, index))
    },
    pagedAccountRows() {
      return this.accountLoading ? [] : this.accountRows
    },
    activeTotal() {
      if (this.activeTab === 'records') {
        return Math.max(0, Number(this.recordPagination.total) || 0)
      }
      if (this.activeTab === 'account') {
        return Math.max(0, Number(this.currentAccountPagination.total) || 0)
      }
      return Math.max(0, Number(this.currentFlowPagination.total) || 0)
    },
    currentPage() {
      if (this.activeTab === 'records') {
        return Math.max(1, Number(this.recordPagination.page) || 1)
      }
      if (this.activeTab === 'account') {
        return Math.max(1, Number(this.currentAccountPagination.page) || 1)
      }
      return Math.max(1, Number(this.currentFlowPagination.page) || 1)
    },
    currentPageSize() {
      if (this.activeTab === 'records') {
        return Math.max(1, Number(this.recordPagination.pageSize) || 10)
      }
      if (this.activeTab === 'account') {
        return Math.max(1, Number(this.currentAccountPagination.pageSize) || 10)
      }
      return Math.max(1, Number(this.currentFlowPagination.pageSize) || 10)
    },
    currentFlowPagination() {
      return this.flowPagination[this.flowCategory] || {
        page: 1,
        pageSize: 10,
        total: 0
      }
    },
    currentAccountPagination() {
      return this.accountPagination[this.accountCategory] || {
        page: 1,
        pageSize: 10,
        total: 0
      }
    },
    flowRecordLabel() {
      return this.flowCategory === 'withdraw' ? '提现记录' : '充值记录'
    },
    accountRecordLabel() {
      return this.accountCategory === 'fuelTransfer' ? '燃料划转记录' : '购买点卡记录'
    },
    accountFinalColumnLabel() {
      return this.accountCategory === 'fuelTransfer' ? '余额 (USDT)' : '支付金额 (USDT)'
    },
    activeTableLoading() {
      if (this.activeTab === 'records') return this.recordLoading
      if (this.activeTab === 'account') return this.accountLoading
      return this.loading
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.activeTotal / this.currentPageSize))
    },
    pageButtons() {
      if (this.pageCount <= 3) {
        return Array.from({ length: this.pageCount }, (_, index) => index + 1)
      }
      const start = Math.min(Math.max(this.currentPage - 1, 1), this.pageCount - 2)
      return [start, start + 1, start + 2]
    },
    summaryRows() {
      return this.monthRecordRows.map((row, index) => this.toRecordRow(row, index))
    },
    serviceFee() {
      const apiTotal = finiteNumber(this.recordSummary && this.recordSummary.allDeposit)
      if (apiTotal !== null) return Math.abs(apiTotal)

      const total = Math.max(0, Number(this.recordPagination.total) || 0)
      const pageSize = Math.max(1, Number(this.recordPagination.pageSize) || 10)
      if (total > pageSize || this.summaryRows.length < total) return null

      const values = this.summaryRows
        .map(row => row.feeValue)
        .filter(value => value !== null)
      return values.length
        ? values.reduce((sum, value) => sum + Math.abs(value), 0)
        : null
    },
    serviceFeeText() {
      return formatNumber(this.serviceFee)
    },
    serviceFeeChange() {
      const previousFee = finiteNumber(
        this.recordSummary && this.recordSummary.previousAllDeposit
      )
      if (this.serviceFee === null || previousFee === null || !previousFee) return null
      return (this.serviceFee - Math.abs(previousFee)) / Math.abs(previousFee) * 100
    },
    serviceFeeChangeText() {
      if (this.serviceFeeChange === null) return '--'
      const sign = this.serviceFeeChange > 0 ? '+' : ''
      return `${sign}${formatNumber(this.serviceFeeChange, 1)}%`
    },
    serviceFeeChangeClass() {
      if (this.serviceFeeChange === null) return 'summary-change is-neutral'
      return `summary-change ${this.serviceFeeChange <= 0 ? 'is-good' : 'is-bad'}`
    },
    settlementProfit() {
      const monthlyProfit = finiteNumber(this.earnings.monthProfit)
      if (monthlyProfit !== null) return monthlyProfit
      const values = this.summaryRows
        .map(row => row.gainValue)
        .filter(value => value !== null)
      return values.length ? values.reduce((sum, value) => sum + value, 0) : null
    },
    settlementProfitText() {
      const value = finiteNumber(this.settlementProfit)
      if (value === null) return '--'
      return `${value > 0 ? '+' : ''}${formatNumber(value)}`
    },
    settlementProfitClass() {
      const value = finiteNumber(this.settlementProfit)
      if (value === null || value === 0) return ''
      return value > 0 ? 'is-positive' : 'is-negative'
    },
    settlementCount() {
      return Math.max(0, Number(this.recordPagination.total) || 0)
    },
    pointCardText() {
      return formatNumber(this.balances.card, 0)
    },
    cardDaysText() {
      const card = finiteNumber(this.balances.card)
      const fee = finiteNumber(this.serviceFee)
      if (card === null || fee === null || fee <= 0) return '按当前消耗预计可用 --'
      const days = Math.max(1, Math.round(card / fee * 30))
      return `按当前消耗预计可用约 ${days} 天`
    }
  },
  watch: {
    period: {
      deep: true,
      handler(value) {
        const year = Number(value && value.year)
        const month = Number(value && value.month)
        if (!year || !month) return
        this.selectedMonth = `${year}-${String(month).padStart(2, '0')}`
      }
    },
    query() { this.resetPage() },
    billType() { this.resetPage() },
    tradeType() { this.resetPage() },
    initialCategory(value) {
      this.syncInitialCategory(value, true)
    }
  },
  mounted() {
    if (this.activeTab === 'flow') {
      this.$emit('tab-change', {
        tab: 'flow',
        category: this.flowCategory
      })
    }
  },
  methods: {
    inSelectedMonth(value) {
      const time = String(value || '')
      return !/^\d{4}-\d{2}$/.test(this.selectedMonth) || time.startsWith(this.selectedMonth)
    },
    shortTime(value) {
      const text = String(value || '')
      const match = text.match(/^\d{4}-(\d{2})-(\d{2})[ T](\d{2}:\d{2})/)
      return match ? `${match[1]}-${match[2]} ${match[3]}` : (text || '--')
    },
    productType(row) {
      const raw = row.raw || {}
      const explicit = firstText(raw, ['product_type', 'strategy_type', 'type']).toLowerCase()
      if (explicit) return explicit
      const transactionType = firstText(raw, ['transaction_type']).toLowerCase()
      if (transactionType === 'future') return 'hedge'
      const userInfo = firstText(raw, ['user_info']).toLowerCase()
      if (transactionType === 'spot') {
        if (userInfo.includes('_bb_')) return 'bb'
        if (userInfo.includes('_grid_')) return 'grid'
        if (userInfo.includes('_martin_')) return 'martin'
      }
      return transactionType
    },
    transactionText(row) {
      const raw = row.raw || {}
      const explicit = firstText(raw, [
        'strategy_name',
        'robot_name',
        'transactionType',
        'content',
        'activity_title'
      ])
      if (explicit) return explicit
      const productType = this.productType(row)
      const productName = PRODUCT_NAMES[productType] || productType
      const coin = this.recordCoin(raw)
      return [productName, coin].filter(Boolean).join(' · ') || row.account || '--'
    },
    recordCoin(raw) {
      const explicit = firstText(raw, ['coin', 'symbol', 'coin_name'])
      if (explicit) return explicit

      const transactionType = firstText(raw, ['transaction_type']).toLowerCase()
      const userInfo = firstText(raw, ['user_info'])
      if (!userInfo) return ''
      const parts = userInfo.split('_')

      if (transactionType === 'future') return parts[3] || ''
      if (transactionType !== 'spot') return ''
      if (parts[2] === 'bb') return parts[4] || ''
      if (parts[2] === 'grid' || parts[2] === 'martin') return parts[6] || ''
      return ''
    },
    recordGainValue(row) {
      const value = firstNumber(row.raw || {}, [
        'profit',
        'trade_profit',
        'income',
        'profit_amount',
        'settlement_profit'
      ])
      if (value !== null) return value
      const amount = finiteNumber(row.amountValue)
      return row.category === 'profit' && amount !== null && amount > 0 ? amount : null
    },
    recordFeeValue(row) {
      const raw = row.raw || {}
      const value = firstNumber(raw, [
        'deposit_change',
        'fee',
        'service_fee',
        'deduction',
        'card_amount'
      ])
      if (value !== null) return value
      const amount = finiteNumber(row.amountValue)
      return row.category === 'fee' && amount !== null ? amount : null
    },
    signedValue(value, forceNegative = false) {
      const number = finiteNumber(value)
      if (number === null) return '--'
      if (forceNegative) return `-${formatNumber(Math.abs(number))}`
      return `${number > 0 ? '+' : ''}${formatNumber(number)}`
    },
    toRecordRow(row, index) {
      const raw = row.raw || {}
      const productType = this.productType(row)
      const gainValue = this.recordGainValue(row)
      const feeValue = this.recordFeeValue(row)
      const hasPointCardBalance =
        raw.fuel_card_balance !== null &&
        raw.fuel_card_balance !== undefined &&
        raw.fuel_card_balance !== ''
      const typeKey = row.category === 'fee' || hasPointCardBalance ? 'fee' : 'profit'
      const balanceValue = firstNumber(
        raw,
        hasPointCardBalance
          ? ['fuel_card_balance', 'deposit_balance']
          : ['deposit_balance', 'fuel_card_balance']
      )
      return {
        key: `${row.id || 'record'}-${index}`,
        rawId: row.id || '',
        time: this.shortTime(row.time),
        typeKey,
        type: typeKey === 'fee' ? '点卡抵扣' : '利润分成',
        typeClass: typeKey === 'fee' ? 'is-card' : 'is-profit',
        gainValue,
        gain: this.signedValue(gainValue),
        feeValue,
        fee: this.signedValue(feeValue, true),
        trade: this.transactionText(row),
        productType,
        balance: balanceValue === null ? (row.balance || '--') : formatNumber(balanceValue)
      }
    },
    recordChannel(row, masked = true) {
      const raw = row.raw || {}
      const isWithdrawal = row.category === 'withdraw'
      const isInternal = Number(raw.new_type) === 2
      const network = firstText(
        raw,
        isWithdrawal
          ? ['network', 'main_chain_detail', 'main_chain', 'chain_type', 'chainType', 'chain', 'protocol']
          : ['main_chain_detail', 'network', 'main_chain', 'chain_type', 'chainType', 'chain', 'protocol']
      ) || (isInternal ? '内部转账' : '')
      const address = firstText(
        raw,
        isWithdrawal
          ? ['cash_address', 'address', 'to_address', 'wallet_address', 'walletAddress']
          : (isInternal
            ? ['chongzhi_pic', 'chongzhi_url', 'address', 'from_address', 'wallet_address', 'walletAddress']
            : ['chongzhi_url', 'chongzhi_pic', 'address', 'from_address', 'wallet_address', 'walletAddress'])
      )
      const networkText = String(network || '').replace(/\s+\(/g, '(')
      const addressText = masked ? this.maskAddress(address) : address
      return [networkText, addressText].filter(Boolean).join(' · ') || '--'
    },
    maskAddress(value) {
      const text = String(value || '')
      if (!text || text.length <= 12) return text
      return `${text.slice(0, 6)}…${text.slice(-4)}`
    },
    statusClass(status) {
      const text = String(status || '')
      if (/完成|成功|到账/.test(text)) return 'is-completed'
      if (/待支付/.test(text)) return 'is-unpaid'
      if (/审核/.test(text)) return 'is-review'
      if (/处理中|待处理|进行中|提交/.test(text)) return 'is-processing'
      return 'is-other'
    },
    toFlowRow(row, index) {
      const amount = finiteNumber(row.amountValue)
      const isWithdrawal = row.category === 'withdraw'
      const normalizedAmount = amount === null
        ? null
        : (isWithdrawal ? -Math.abs(amount) : Math.abs(amount))
      return {
        key: `${row.id || 'flow'}-${index}`,
        time: this.shortTime(row.time),
        kind: Number((row.raw || {}).new_type) === 2
          ? '内部转账'
          : (isWithdrawal ? '提现' : '充值'),
        account: row.account || '资金账户',
        coin: row.coin || firstText(row.raw || {}, ['coin', 'currency', 'token']) || 'USDT',
        amount: this.signedValue(normalizedAmount),
        amountClass: isWithdrawal ? 'negative' : 'positive',
        channel: this.recordChannel(row),
        fullChannel: this.recordChannel(row, false),
        status: row.status || '--',
        statusClass: this.statusClass(row.status)
      }
    },
    toAccountRecordRow(row, index) {
      const raw = row && typeof row === 'object' ? row : {}
      const isTransfer = this.accountCategory === 'fuelTransfer'
      const paidAmount = firstNumber(raw, ['card_amount', 'amount'])
      const bonusAmount = firstNumber(raw, ['give_card_amount', 'bonus_amount'])
      const amount = isTransfer
        ? firstNumber(raw, ['money', 'amount'])
        : (paidAmount === null
          ? null
          : paidAmount + (bonusAmount === null ? 0 : bonusAmount))
      const balance = isTransfer
        ? firstNumber(raw, ['to_balance', 'balance'])
        : paidAmount
      const time = firstText(
        raw,
        isTransfer
          ? ['update_time', 'create_time', 'time']
          : ['create_time', 'update_time', 'time']
      )
      const kind = isTransfer
        ? '燃料划转'
        : (firstText(raw, ['activity_title', 'title']) || '购买点卡')
      const account = isTransfer
        ? (amount === null
          ? '资金账户 / 燃料账户'
          : (amount < 0 ? '资金账户 → 燃料账户' : '燃料账户 → 资金账户'))
        : '点卡账户'
      const id = firstText(raw, ['id', 'uid', 'order_no', 'orderNo']) || index
      return {
        key: `${isTransfer ? 'fuel-transfer' : 'point-card'}-${id}-${index}`,
        time: this.shortTime(time),
        kind,
        account,
        coin: firstText(raw, ['coin', 'currency', 'token']) || 'USDT',
        amount: this.signedValue(amount),
        amountClass: amount !== null && amount < 0 ? 'negative' : 'positive',
        balance: balance === null ? '--' : formatNumber(balance)
      }
    },
    changeTab(tab) {
      if (this.activeTab === tab) return
      this.activeTab = tab
      this.$emit('tab-change', {
        tab,
        category: tab === 'account' ? this.accountCategory : this.flowCategory
      })
    },
    changeFlowCategory(category) {
      const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
      this.flowCategory = normalizedCategory
      this.$emit('flow-category-change', {
        category: normalizedCategory,
        page: 1,
        pageSize: 10
      })
    },
    changeAccountCategory() {
      this.$emit('account-category-change', {
        category: this.accountCategory,
        page: 1,
        pageSize: 10
      })
    },
    handleMonthChange() {
      const parts = this.selectedMonth.split('-')
      const period = {
        year: Number(parts[0]),
        month: Number(parts[1])
      }
      this.$emit('period-change', period)
      this.$emit('record-period-change', period)
    },
    goPage(page) {
      if (page < 1 || page > this.pageCount) return
      if (this.activeTab === 'records') {
        if (page === this.currentPage || this.recordLoading) return
        this.$emit('record-page', { page, pageSize: 10 })
        return
      }
      if (this.activeTab === 'account') {
        if (page === this.currentPage || this.accountLoading) return
        this.$emit('account-page', {
          category: this.accountCategory,
          page,
          pageSize: 10
        })
        return
      }
      if (page === this.currentPage || this.loading) return
      this.$emit('flow-page', {
        category: this.flowCategory,
        page,
        pageSize: 10
      })
    },
    resetPage() {
      if (this.activeTab === 'records') {
        if (this.currentPage !== 1 && !this.recordLoading) {
          this.$emit('record-page', { page: 1, pageSize: 10 })
        }
        return
      }
      if (this.currentPage !== 1 && !this.loading) {
        this.$emit('flow-page', {
          category: this.flowCategory,
          page: 1,
          pageSize: 10
        })
      }
    },
    syncInitialCategory(value, notify = false) {
      const category = String(value || '')
      if (['deposit', 'withdraw'].includes(category)) {
        this.activeTab = 'flow'
        this.flowCategory = category
        if (notify) {
          this.$emit('tab-change', {
            tab: 'flow',
            category
          })
        }
        return
      }

      this.activeTab = 'records'
      this.billType = category === 'fee'
        ? 'fee'
        : (category === 'profit' ? 'profit' : 'all')
      if (notify) {
        this.$emit('tab-change', {
          tab: 'records',
          category
        })
      }
    },
    csvCell(value) {
      return `"${String(value === null || value === undefined ? '' : value).replace(/"/g, '""')}"`
    },
    exportBills() {
      const headers = this.activeTab === 'records'
        ? ['时间', '账单类型', '收益 (USDT)', '扣除', '交易类型', '余额 (USDT)'].map(label => this.$t(label))
        : (this.activeTab === 'account'
          ? ['时间', '记录类型', '账户', '币种', '数量', this.accountFinalColumnLabel].map(label => this.$t(label))
          : ['时间', '订单类型', '账户', '币种', '数量', '渠道 · 地址', '状态'].map(label => this.$t(label)))
      const source = this.activeTab === 'records'
        ? this.recordRows
        : (this.activeTab === 'account' ? this.accountRows : this.flowRows)
      const rows = source.map(row => this.activeTab === 'records'
        ? [row.time, this.$t(row.type), row.gain, row.fee, this.$t(row.trade), row.balance]
        : (this.activeTab === 'account'
          ? [row.time, this.$t(row.kind), this.$t(row.account), row.coin, row.amount, row.balance]
          : [row.time, this.$t(row.kind), this.$t(row.account), row.coin, row.amount, row.fullChannel, this.$t(row.status)])
      )
      const csv = [headers, ...rows]
        .map(line => line.map(this.csvCell).join(','))
        .join('\r\n')
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `UPlus-${this.$t('账单')}-${this.selectedMonth}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.bills-panel {
  display: flex;
  color: #1b1f2a;
  flex-direction: column;
  font-family: Manrope, "PingFang SC", "Microsoft YaHei", sans-serif;
}

.bills-heading {
  display: flex;
  min-height: 46px;
  margin-bottom: 20px;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.bills-heading h1 {
  margin: 0;
  color: #101828;
  font-size: 24px;
  line-height: 30px;
  font-weight: 800;
  letter-spacing: .2px;
}

.bills-heading p {
  margin: 5px 0 0;
  color: #8a90a0;
  font-size: 13px;
  line-height: 18px;
}

.bills-heading__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bills-heading__actions select {
  width: 108px;
  height: 40px;
  padding: 0 34px 0 12px;
  color: #3a3f4e;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  outline: none;
  background-color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.export-button {
  height: 40px;
  padding: 0 18px;
  color: #fff;
  border: 0;
  border-radius: 8px;
  background: #1f6bff;
  font-size: 14px;
  font-weight: 700;
  transition: background .16s ease;
}

.export-button:hover { background: #1558db; }

.bills-heading__actions select:disabled,
.export-button:disabled {
  cursor: wait;
  opacity: .58;
}

.export-button:disabled:hover { background: #1f6bff; }

.bill-summary {
  display: grid;
  min-height: 146px;
  margin-bottom: 20px;
  overflow: hidden;
  grid-template-columns: repeat(3, 1fr);
  border-color: #e7eaf1;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 6px 20px rgba(16, 24, 40, .04);
}

.bill-summary article {
  min-width: 0;
  padding: 22px 26px;
}

.bill-summary article + article { border-left: 1px solid #f0f2f6; }

.summary-title {
  display: flex;
  min-width: 0;
  color: #79808f;
  align-items: center;
  gap: 9px;
  font-size: 13.5px;
  line-height: 26px;
  font-weight: 500;
}

.summary-icon {
  display: inline-grid;
  width: 26px;
  height: 26px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 8px;
  font-size: 14px;
}

.summary-icon.is-fee { color: #b45309; background: #fbf1df; }
.summary-icon.is-profit { color: #0b9b57; background: #e8f6ee; }
.summary-icon.is-card { color: #1f6bff; background: #ebf1fe; }

.summary-title em {
  display: inline-flex;
  margin-left: auto;
  padding: 3px 9px;
  color: #0b7a47;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  background: #f0faf4;
  font-size: 11px;
  line-height: 16px;
  font-style: normal;
  font-weight: 800;
}

.summary-title em i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #12b76a;
  animation: up-pulse 2s ease infinite;
}

.bill-summary article > strong {
  display: block;
  margin-top: 8px;
  color: #101828;
  font-size: 30px;
  line-height: 38px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: -.3px;
}

.bill-summary article > strong.is-positive { color: #0b9b57; }
.bill-summary article > strong.is-negative { color: #e5484d; }

.bill-summary article > strong small {
  color: #8a90a0;
  font-size: 15px;
  font-weight: 600;
}

.bill-summary article > p {
  min-height: 20px;
  margin: 6px 0 0;
  color: #8a90a0;
  font-size: 13px;
  line-height: 20px;
}

.bill-summary article > p small { font-size: 13px; }

.summary-change {
  margin-right: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.summary-change.is-good { color: #0b7a47; background: #e8f6ee; }
.summary-change.is-bad { color: #b42318; background: #fef0ef; }
.summary-change.is-neutral { color: #79808f; background: #f2f4f7; }

.bill-detail-title {
  display: flex;
  min-height: 24px;
  margin: 6px 0 14px;
  align-items: center;
  gap: 12px;
}

.bill-detail-title > i {
  width: 4px;
  height: 20px;
  border-radius: 99px;
  background: #0e9f5c;
}

.bill-detail-title h2 {
  margin: 0;
  color: #101828;
  font-size: 18px;
  line-height: 24px;
  font-weight: 800;
  letter-spacing: .2px;
}

.bill-detail-title span {
  padding: 3px 11px;
  color: #0b7a47;
  border-radius: 999px;
  background: #e8f6ee;
  font-size: 11.5px;
  line-height: 16px;
  font-weight: 800;
}

.bill-detail {
  min-height: 460px;
  overflow: hidden;
  border-color: #e7eaf1;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 6px 20px rgba(16, 24, 40, .04);
}

.bill-toolbar {
  display: flex;
  min-height: 70px;
  padding: 18px 24px 14px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.bill-tabs {
  display: flex;
  padding: 3px;
  gap: 4px;
  border-radius: 9px;
  background: #f5f6f9;
}

.bill-tabs button {
  height: 32px;
  padding: 0 16px;
  color: #687083;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.bill-tabs button.active {
  color: #1f6bff;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .04);
}

.bill-tab-label--mobile {
  display: none;
}

.flow-switch {
  display: flex;
  padding: 3px;
  gap: 4px;
  border-radius: 9px;
  background: #f5f6f9;
}

.flow-switch button {
  height: 32px;
  padding: 0 16px;
  color: #687083;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-size: 13px;
  line-height: 32px;
  font-weight: 700;
  white-space: nowrap;
}

.flow-switch button.active {
  color: #1f6bff;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .04);
}

.flow-switch button:disabled {
  cursor: wait;
  opacity: .55;
}

.account-record-filter {
  display: flex;
  color: #687083;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
}

.account-record-filter select {
  min-width: 132px;
  height: 37px;
  padding: 0 34px 0 12px;
  color: #3a3f4e;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  outline: none;
  background-color: #fff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}

.account-record-filter select:disabled {
  cursor: wait;
  opacity: .55;
}

.bill-filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bill-filters input,
.bill-filters select {
  height: 37px;
  color: #3a3f4e;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  outline: none;
  background-color: #fff;
  font-size: 13px;
}

.bill-filters input {
  width: 180px;
  padding: 0 12px;
}

.bill-filters input::placeholder { color: #98a0af; }

.bill-filters select {
  min-width: 94px;
  padding: 0 34px 0 11px;
  font-weight: 600;
  cursor: pointer;
}

.records-table__head,
.records-table__row,
.flow-table__head,
.flow-table__row,
.account-record-table__head,
.account-record-table__row {
  display: grid;
  padding: 0 24px;
  align-items: center;
  gap: 10px;
}

.records-table__head,
.records-table__row {
  grid-template-columns: 1.1fr 1fr .9fr .9fr 1.8fr 1.1fr;
}

.flow-table__head,
.flow-table__row {
  grid-template-columns: 1fr .8fr 1fr .7fr 1fr 1.8fr .9fr;
}

.account-record-table__head,
.account-record-table__row {
  grid-template-columns: 1fr 1.35fr 1.4fr .7fr 1fr 1fr;
}

.records-table__head,
.flow-table__head,
.account-record-table__head {
  min-height: 40px;
  color: #8a90a0;
  border-bottom: 1px solid #f0f2f6;
  font-size: 12.5px;
  line-height: 18px;
  font-weight: 600;
}

.records-table__head span:last-child,
.account-record-table__head span:last-child { text-align: right; }

.records-table__row,
.flow-table__row,
.account-record-table__row {
  min-height: 48px;
  color: #3a3f4e;
  border-bottom: 1px solid #f6f7fa;
  font-size: 14px;
  line-height: 19px;
  transition: background .15s ease;
}

.records-table__row:hover,
.flow-table__row:hover,
.account-record-table__row:hover { background: #fafafd; }

.records-table__row > span:first-child,
.flow-table__row > span:first-child,
.account-record-table__row > span:first-child {
  color: #5a6072;
  font-variant-numeric: tabular-nums;
}

.records-table__row strong,
.flow-table__row strong,
.account-record-table__row strong { font-weight: 700; }

.positive { color: #0b9b57; font-variant-numeric: tabular-nums; }
.negative { color: #d92d20; font-variant-numeric: tabular-nums; }
.align-right { text-align: right; font-variant-numeric: tabular-nums; }

.bill-type {
  display: inline-flex;
  padding: 3px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  line-height: 18px;
  font-style: normal;
  font-weight: 700;
}

.bill-type.is-profit { color: #0b7a47; background: #e8f6ee; }
.bill-type.is-card { color: #1550cc; background: #e7effe; }

.flow-channel {
  overflow: hidden;
  color: #5a6072;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
}

.flow-status {
  display: inline-flex;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  line-height: 18px;
  font-style: normal;
  font-weight: 700;
}

.flow-status.is-completed { color: #0b7a47; background: #e8f6ee; }
.flow-status.is-unpaid { color: #d92d20; background: #feeeee; }
.flow-status.is-review { color: #b54708; background: #fff3e0; }
.flow-status.is-processing { color: #1550cc; background: #eaf1ff; }
.flow-status.is-other { color: #5a6072; background: #f1f3f7; }

.bill-empty {
  display: grid;
  min-height: 288px;
  color: #98a0af;
  place-items: center;
  font-size: 13.5px;
}

.bill-pagination {
  display: flex;
  min-height: 66px;
  padding: 16px 24px;
  color: #98a0af;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}

.bill-pagination > div {
  display: flex;
  align-items: center;
  gap: 6px;
}

.bill-pagination button {
  min-width: 34px;
  height: 34px;
  padding: 0 13px;
  color: #3a3f4e;
  border: 1px solid #dcdfe8;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
}

.bill-pagination button:not(:disabled):hover {
  color: #1f6bff;
  border-color: #a9c4f7;
}

.bill-pagination button.active {
  color: #fff;
  border-color: #1f6bff;
  background: #1f6bff;
  font-weight: 700;
}

.bill-pagination button:disabled {
  cursor: not-allowed;
  opacity: .45;
}

@media (max-width: 1160px) {
  .bill-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
  .records-table__head,
  .flow-table__head,
  .account-record-table__head { display: none; }
  .records-table__row { grid-template-columns: 130px 110px 1fr 100px; }
  .records-table__row > :nth-child(4),
  .records-table__row > :nth-child(6) { display: none; }
  .flow-table__row { grid-template-columns: 130px 80px 1fr 100px; }
  .flow-table__row > :nth-child(3),
  .flow-table__row > :nth-child(4),
  .flow-table__row > :nth-child(6) { display: none; }
  .account-record-table__row { grid-template-columns: 130px 1.3fr 1fr 100px; }
  .account-record-table__row > :nth-child(3),
  .account-record-table__row > :nth-child(4) { display: none; }
}

@media (max-width: 760px) {
  .bill-tab-label--desktop {
    display: none;
  }
  .bill-tab-label--mobile {
    display: inline;
    white-space: nowrap;
  }
  .bill-toolbar {
    width: 100%;
    box-sizing: border-box;
  }
  .bill-tabs {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
  }
  .bill-tabs button {
    width: 100%;
    min-width: 0;
    min-height: 54px;
    padding: 6px 4px;
    white-space: normal;
    line-height: 1.35;
  }
  .bills-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .bills-heading__actions { width: 100%; }
  .bills-heading__actions select,
  .export-button { flex: 1; }
  .bill-summary { grid-template-columns: 1fr; }
  .bill-summary article + article {
    border-top: 1px solid #f0f2f6;
    border-left: 0;
  }
  .bill-filters {
    width: 100%;
    flex-wrap: wrap;
  }
  .bill-filters input { width: 100%; }
  .bill-filters select { flex: 1; }
  .account-record-filter {
    width: 100%;
    justify-content: space-between;
  }
  .account-record-filter select { flex: 1; }
  .records-table__row,
  .flow-table__row,
  .account-record-table__row {
    grid-template-columns: 1fr 1fr;
    padding: 12px 16px;
  }
  .records-table__row > :nth-child(5),
  .flow-table__row > :nth-child(5) { grid-column: 1 / -1; }
  .account-record-table__row > :nth-child(5) { grid-column: auto; }
  .bill-pagination {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
